import React, { useState, useEffect } from 'react';
import Header from '../../components/Header/Header';
import { appUrl, withAppBasename } from '../../config/appBase';
import LandingAppStoreFooter from '../../features/apps/components/LandingAppStoreFooter';
import { certificateAPI } from '../../features/certificates/api/certificateAPI';
import { useAuth } from '../../features/auth/hooks/useAuth';
import { useAdminAccess } from '../../features/certificates/hooks/useAdminAccess';

const PROGRAMME_PRESETS = [
  'Medical Work Experience Week',
  'LNAT & Oxford Legal Reasoning Programme',
  'TMUA Advanced Mathematical Thinking',
  'Oxbridge Interview & Critical Reasoning Lab',
  'Quantitative Finance & Case Analysis Track'
];

export default function AdminContentManagerPage({ defaultTab = 'content' }) {
  const { user } = useAuth();
  const adminAccess = useAdminAccess(user);

  // Emergency passcode state (only offered while the backend has it switched on)
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    return sessionStorage.getItem('ivori_admin_auth') === 'true';
  });
  const [passcodeInput, setPasscodeInput] = useState('');
  const [authError, setAuthError] = useState('');
  const [authenticating, setAuthenticating] = useState(false);
  const [adminPasscode, setAdminPasscode] = useState(() => {
    return sessionStorage.getItem('ivori_admin_passcode') || '';
  });

  // Tab & content state
  const [activeAdminTab, setActiveAdminTab] = useState(defaultTab);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterCategory, setFilterCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [savingId, setSavingId] = useState(null);
  const [toastMsg, setToastMsg] = useState('');

  // Certificate issuance state
  const [certStudentName, setCertStudentName] = useState('');
  const [certStudentEmail, setCertStudentEmail] = useState('');
  const [certProgramme, setCertProgramme] = useState(PROGRAMME_PRESETS[0]);
  const [certCustomProgramme, setCertCustomProgramme] = useState('');
  const [certDate, setCertDate] = useState(() => {
    const now = new Date();
    return now.toISOString().split('T')[0];
  });
  const [certDuration, setCertDuration] = useState('35 hours');
  const [certCustomId, setCertCustomId] = useState('');
  const [certSkills, setCertSkills] = useState('Clinical Observation, Medical Ethics, Patient Care, First Aid');
  const [issuingCert, setIssuingCert] = useState(false);
  const [issuedCertResult, setIssuedCertResult] = useState(null);
  const [certificatesList, setCertificatesList] = useState([]);
  const [loadingCerts, setLoadingCerts] = useState(false);
  const [studentSuggestions, setStudentSuggestions] = useState([]);
  const [searchingStudents, setSearchingStudents] = useState(false);

  const isUnlocked = adminAccess.isAdmin || (isAuthenticated && adminAccess.passcodeEnabled);

  const apiUrl = process.env.REACT_APP_API_URL || '';

  const adminApiHeaders = (extra = {}) => {
    const token =
      localStorage.getItem('pathbuddy_access_token') ||
      sessionStorage.getItem('pathbuddy_access_token') ||
      localStorage.getItem('token');
    const headers = { ...extra };
    if (token) headers.Authorization = `Bearer ${token}`;
    const pass = adminPasscode || sessionStorage.getItem('ivori_admin_passcode') || '';
    if (pass) headers['X-Admin-Passcode'] = pass;
    return headers;
  };

  const handlePasscodeSubmit = async (e) => {
    e.preventDefault();
    setAuthError('');
    setAuthenticating(true);

    const entered = passcodeInput.trim();
    if (!entered) {
      setAuthError('Please enter the admin passcode.');
      setAuthenticating(false);
      return;
    }

    try {
      const res = await fetch(`${apiUrl}/api/certificates/verify-passcode`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ passcode: entered })
      });
      const data = await res.json();

      if (res.ok && data.success) {
        sessionStorage.setItem('ivori_admin_auth', 'true');
        sessionStorage.setItem('ivori_admin_passcode', entered);
        setAdminPasscode(entered);
        setIsAuthenticated(true);
        setPasscodeInput('');
      } else {
        setAuthError(data.error || 'Invalid administrator passcode.');
      }
    } catch (err) {
      setAuthError('Could not verify administrator passcode. Please try again.');
    } finally {
      setAuthenticating(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('ivori_admin_auth');
    sessionStorage.removeItem('ivori_admin_passcode');
    setAdminPasscode('');
    setIsAuthenticated(false);
    setPasscodeInput('');
  };

  const fetchItems = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${apiUrl}/api/admin/content-items`, {
        headers: adminApiHeaders(),
      });
      const data = await res.json();
      setItems(data.items || []);
    } catch (err) {
      console.error("Failed to fetch admin content items:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchCertificates = async () => {
    try {
      setLoadingCerts(true);
      const list = await certificateAPI.listCertificates(100);
      setCertificatesList(list);
    } catch (err) {
      console.error("Failed to list certificates:", err);
    } finally {
      setLoadingCerts(false);
    }
  };

  useEffect(() => {
    if (isUnlocked) {
      if (activeAdminTab === 'content') {
        fetchItems();
      } else if (activeAdminTab === 'certificates') {
        fetchCertificates();
      }
    }
  }, [isUnlocked, activeAdminTab]);

  const showToast = (msg) => {
    setToastMsg(msg);
    setTimeout(() => setToastMsg(''), 3500);
  };

  const handleToggleActive = async (item) => {
    const newActiveState = !item.is_active;
    setSavingId(item.id);

    try {
      const res = await fetch(`${apiUrl}/api/admin/toggle-content-access`, {
        method: 'POST',
        headers: adminApiHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          item_id: item.id,
          item_type: item.item_type,
          category_tag: item.category_tag,
          is_active: newActiveState
        })
      });

      if (res.ok) {
        setItems(prev => prev.map(i => i.id === item.id ? { ...i, is_active: newActiveState } : i));
        showToast(`Updated "${item.title}" active status to ${newActiveState ? 'ON' : 'OFF'}`);
      }
    } catch (err) {
      showToast('Error updating item access');
    } finally {
      setSavingId(null);
    }
  };

  const handleChangeCategory = async (item, newCategory) => {
    setSavingId(item.id);

    try {
      const res = await fetch(`${apiUrl}/api/admin/toggle-content-access`, {
        method: 'POST',
        headers: adminApiHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          item_id: item.id,
          item_type: item.item_type,
          category_tag: newCategory,
          is_active: item.is_active
        })
      });

      if (res.ok) {
        setItems(prev => prev.map(i => i.id === item.id ? { ...i, category_tag: newCategory } : i));
        showToast(`Assigned "${item.title}" to ${newCategory.toUpperCase()} path`);
      }
    } catch (err) {
      showToast('Error updating category tag');
    } finally {
      setSavingId(null);
    }
  };

  const handleStudentSearch = async (val) => {
    setCertStudentName(val);
    if (!val || val.length < 2) {
      setStudentSuggestions([]);
      return;
    }
    try {
      setSearchingStudents(true);
      const results = await certificateAPI.searchStudents(val);
      setStudentSuggestions(results);
    } catch (e) {
      console.error('Error searching students', e);
    } finally {
      setSearchingStudents(false);
    }
  };

  const handleSelectStudent = (s) => {
    setCertStudentName(s.full_name || s.email);
    setCertStudentEmail(s.email || '');
    setStudentSuggestions([]);
  };

  const handleIssueCertificate = async (e) => {
    e.preventDefault();
    if (!certStudentName.trim() && !certStudentEmail.trim()) {
      showToast('Student Name or Email is required.');
      return;
    }

    const effectiveProg = certProgramme === 'custom' ? certCustomProgramme : certProgramme;
    if (!effectiveProg.trim()) {
      showToast('Programme Title is required.');
      return;
    }

    setIssuingCert(true);
    setIssuedCertResult(null);

    try {
      const skillsArray = certSkills.split(',').map(s => s.trim()).filter(Boolean);
      const res = await certificateAPI.issueCertificate({
        student_name: certStudentName.trim(),
        student_email: certStudentEmail.trim() || undefined,
        programme_name: effectiveProg.trim(),
        issue_date: certDate.trim(),
        duration: certDuration.trim(),
        id: certCustomId.trim() || undefined,
        skills: skillsArray
      });

      if (res.success && res.certificate) {
        setIssuedCertResult(res.certificate);
        showToast(`Certificate issued for ${res.certificate.student_name}!`);
        fetchCertificates();
      } else {
        showToast(res.error || 'Failed to issue certificate');
      }
    } catch (err) {
      showToast(err.message || 'Error issuing certificate');
    } finally {
      setIssuingCert(false);
    }
  };

  const copyVerifyLink = (id) => {
    const link = appUrl(`/verify/${id}`);
    navigator.clipboard.writeText(link).then(() => {
      showToast('Verification link copied to clipboard!');
    }).catch(() => {
      showToast('Unable to copy link.');
    });
  };

  const filteredItems = items.filter(item => {
    const matchesSearch = item.title.toLowerCase().includes(searchQuery.toLowerCase()) || 
                          item.subject.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          item.id.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = filterCategory === 'all' || item.category_tag === filterCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', background: 'var(--iv-bg)', color: 'var(--iv-body)' }}>
      <Header />

      {/* =========================================================================
          PASSCODE LOCK SCREEN (RENDERED WHEN NOT AUTHENTICATED)
         ========================================================================= */}
      {!isUnlocked ? (
        <main style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 20px' }}>
          <div style={{
            maxWidth: '440px',
            width: '100%',
            backgroundColor: 'var(--iv-bg)',
            borderRadius: '16px',
            border: '1px solid var(--iv-line)',
            padding: '36px 32px',
            boxShadow: '0 12px 32px color-mix(in srgb, var(--iv-shadow) 6%, transparent)',
            textAlign: 'center'
          }}>
            <div style={{
              width: '52px',
              height: '52px',
              backgroundColor: 'var(--iv-bg)',
              border: '1px solid var(--iv-line)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px',
              color: 'var(--iv-body)'
            }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
              </svg>
            </div>

            <h1 style={{ fontFamily: 'var(--iv-font-head)', fontSize: '24px', margin: '0 0 8px', color: 'var(--iv-body)' }}>
              {adminAccess.checking ? 'Checking access…' : 'Administrator Access Required'}
            </h1>
            <p style={{ fontFamily: 'var(--iv-font-body)', fontSize: '14px', color: 'var(--iv-muted)', margin: '0 0 24px', lineHeight: 1.5 }}>
              {adminAccess.passcodeEnabled
                ? 'Sign in with an administrator account, or enter the emergency passcode.'
                : 'Sign in with an administrator account to access the admin console, content manager, and certificate issuer.'}
            </p>

            {authError && (
              <div style={{
                backgroundColor: 'var(--iv-bg)',
                color: 'var(--iv-danger)',
                padding: '10px 14px',
                borderRadius: '8px',
                fontSize: '13px',
                fontWeight: 600,
                marginBottom: '16px'
              }}>
                {authError}
              </div>
            )}

            {adminAccess.passcodeEnabled && (
            <form onSubmit={handlePasscodeSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <input
                type="password"
                required
                autoFocus
                placeholder="Enter passcode..."
                value={passcodeInput}
                onChange={(e) => setPasscodeInput(e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  borderRadius: '8px',
                  border: '1.5px solid var(--iv-line)',
                  fontSize: '15px',
                  boxSizing: 'border-box',
                  outline: 'none',
                  textAlign: 'center',
                  letterSpacing: '2px'
                }}
              />

              <button
                type="submit"
                disabled={authenticating}
                style={{
                  backgroundColor: 'var(--iv-btn)',
                  color: 'var(--iv-btn-fg)',
                  border: 'none',
                  padding: '12px',
                  borderRadius: '8px',
                  fontWeight: 700,
                  fontSize: '14px',
                  cursor: authenticating ? 'not-allowed' : 'pointer',
                  opacity: authenticating ? 0.7 : 1
                }}
              >
                {authenticating ? 'Verifying...' : 'Unlock Admin Console'}
              </button>
            </form>
            )}
          </div>
        </main>
      ) : (

        /* =========================================================================
            AUTHENTICATED ADMIN CONSOLE
           ========================================================================= */
        <main style={{ flex: 1, maxWidth: '1200px', margin: '40px auto', padding: '0 24px', width: '100%', boxSizing: 'border-box' }}>
          
          {/* Top Bar with Tabs and Lock Button */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '2px solid var(--iv-line)', marginBottom: '28px', paddingBottom: '8px', flexWrap: 'wrap', gap: '12px' }}>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                type="button"
                onClick={() => setActiveAdminTab('content')}
                style={{
                  padding: '10px 20px',
                  border: 'none',
                  background: 'none',
                  fontSize: '16px',
                  fontWeight: 700,
                  color: activeAdminTab === 'content' ? 'var(--iv-body)' : 'var(--iv-muted)',
                  borderBottom: activeAdminTab === 'content' ? '3px solid var(--iv-head)' : '3px solid transparent',
                  cursor: 'pointer',
                  marginBottom: '-10px'
                }}
              >
                Plan Content & Entitlements
              </button>
              <button
                type="button"
                onClick={() => setActiveAdminTab('certificates')}
                style={{
                  padding: '10px 20px',
                  border: 'none',
                  background: 'none',
                  fontSize: '16px',
                  fontWeight: 700,
                  color: activeAdminTab === 'certificates' ? 'var(--iv-body)' : 'var(--iv-muted)',
                  borderBottom: activeAdminTab === 'certificates' ? '3px solid var(--iv-head)' : '3px solid transparent',
                  cursor: 'pointer',
                  marginBottom: '-10px'
                }}
              >
                Certificates
              </button>
            </div>

            <button
              type="button"
              onClick={handleLogout}
              style={{
                padding: '6px 14px',
                background: 'var(--iv-surface)',
                border: '1px solid var(--iv-line)',
                borderRadius: '6px',
                fontSize: '12px',
                fontWeight: 600,
                color: 'var(--iv-muted)',
                cursor: 'pointer'
              }}
            >
              Lock Console
            </button>
          </div>

          {/* Toast Notification Banner */}
          {toastMsg && (
            <div style={{ background: 'var(--iv-btn)', color: 'var(--iv-btn-fg)', padding: '12px 20px', borderRadius: '12px', marginBottom: '20px', fontSize: '14px', fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'space-between', boxShadow: '0 8px 24px color-mix(in srgb, var(--iv-shadow) 12%, transparent)' }}>
              <span>{toastMsg}</span>
              <span style={{ color: 'var(--iv-success)' }}>✓ Saved</span>
            </div>
          )}

          {/* =========================================================================
              TAB 1: CONTENT & ENTITLEMENTS
             ========================================================================= */}
          {activeAdminTab === 'content' && (
            <>
              {/* Header Title & Badge */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '28px', flexWrap: 'wrap', gap: '16px' }}>
                <div>
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: 'var(--iv-fill)', color: 'var(--iv-head)', padding: '4px 12px', borderRadius: '999px', fontSize: '12px', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', marginBottom: '8px' }}>
                    Launch Admin Control Panel
                  </div>
                  <h1 style={{ fontFamily: 'var(--iv-font-head)', fontSize: '32px', margin: 0, color: 'var(--iv-body)' }}>
                    Plan Content & Test Entitlements
                  </h1>
                  <p style={{ fontFamily: 'var(--iv-font-body)', fontSize: '15px', color: 'var(--iv-muted)', margin: '6px 0 0 0' }}>
                    Toggle visibility (On/Off) and assign which mock papers, cases, and interactive tools belong to LNAT vs TMUA plans.
                  </p>
                </div>

                <button
                  type="button"
                  onClick={fetchItems}
                  style={{ padding: '10px 20px', background: 'var(--iv-surface)', border: '1px solid var(--iv-line)', borderRadius: '999px', fontWeight: 700, fontSize: '14px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '8px' }}
                >
                  Refresh List
                </button>
              </div>

              {/* Filters & Search Controls */}
              <div style={{ background: 'var(--iv-raised)', padding: '20px 24px', borderRadius: '16px', border: '1px solid var(--iv-line)', marginBottom: '24px', display: 'flex', gap: '16px', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                
                {/* Category Filter Tabs */}
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {[
                    { key: 'all', label: 'All Items' },
                    { key: 'lnat', label: 'LNAT Path' },
                    { key: 'tmua', label: 'TMUA Path' },
                    { key: 'general', label: 'General Workspace' }
                  ].map(tab => (
                    <button
                      key={tab.key}
                      type="button"
                      onClick={() => setFilterCategory(tab.key)}
                      style={{
                        padding: '8px 16px',
                        borderRadius: '999px',
                        border: filterCategory === tab.key ? '1px solid var(--iv-accent)' : '1px solid var(--iv-line)',
                        background: filterCategory === tab.key ? 'var(--iv-btn)' : 'var(--iv-surface)',
                        color: filterCategory === tab.key ? 'var(--iv-btn-fg)' : 'var(--iv-body)',
                        fontWeight: 700,
                        fontSize: '13.5px',
                        cursor: 'pointer'
                      }}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>

                {/* Search Box */}
                <div style={{ flex: '1', maxWidth: '320px', position: 'relative' }}>
                  <input
                    type="text"
                    placeholder="Search tests, cases, apps..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '10px 16px',
                      borderRadius: '999px',
                      border: '1px solid var(--iv-line)',
                      fontSize: '14px',
                      outline: 'none',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
              </div>

              {/* Content Items Table */}
              {loading ? (
                <div style={{ textAlign: 'center', padding: '60px', color: 'var(--iv-muted)' }}>Loading content items...</div>
              ) : filteredItems.length === 0 ? (
                <div style={{ background: 'var(--iv-surface)', padding: '48px', borderRadius: '16px', textAlign: 'center', border: '1px solid var(--iv-line)', color: 'var(--iv-muted)' }}>
                  No items match your filter criteria.
                </div>
              ) : (
                <div style={{ background: 'var(--iv-surface)', borderRadius: '16px', border: '1px solid var(--iv-line)', overflow: 'hidden', boxShadow: '0 4px 16px color-mix(in srgb, var(--iv-shadow) 3%, transparent)' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '14px' }}>
                    <thead>
                      <tr style={{ background: 'var(--iv-bg)', borderBottom: '1px solid var(--iv-line)', color: 'var(--iv-muted)', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                        <th style={{ padding: '16px 20px' }}>Item Title & Details</th>
                        <th style={{ padding: '16px 20px' }}>Type</th>
                        <th style={{ padding: '16px 20px' }}>Assigned Path</th>
                        <th style={{ padding: '16px 20px', textAlign: 'center' }}>Live Visibility (On/Off)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredItems.map((item, idx) => (
                        <tr key={item.id} style={{ borderBottom: idx === filteredItems.length - 1 ? 'none' : '1px solid var(--iv-line)', opacity: item.is_active ? 1 : 0.6 }}>
                          <td style={{ padding: '16px 20px' }}>
                            <div style={{ fontWeight: 700, color: 'var(--iv-body)', fontSize: '15px' }}>{item.title}</div>
                            <div style={{ fontSize: '12.5px', color: 'var(--iv-muted)', marginTop: '2px' }}>
                              ID: <code style={{ background: 'var(--iv-bg)', padding: '2px 6px', borderRadius: '4px', border: '1px solid var(--iv-line)' }}>{item.id}</code> • Subject: {item.subject}
                            </div>
                          </td>
                          <td style={{ padding: '16px 20px' }}>
                            <span style={{
                              display: 'inline-block',
                              padding: '4px 10px',
                              borderRadius: '999px',
                              fontSize: '12px',
                              fontWeight: 700,
                              background: item.item_type === 'case_study' ? 'var(--iv-bg)' : 'var(--iv-bg)',
                              color: item.item_type === 'case_study' ? 'var(--iv-accent)' : 'var(--iv-accent)'
                            }}>
                              {item.item_type === 'case_study' ? 'Case Study' : 'Interactive App'}
                            </span>
                          </td>
                          <td style={{ padding: '16px 20px' }}>
                            <select
                              value={item.category_tag}
                              onChange={(e) => handleChangeCategory(item, e.target.value)}
                              disabled={savingId === item.id}
                              style={{
                                padding: '8px 12px',
                                borderRadius: '8px',
                                border: '1px solid var(--iv-line)',
                                background: 'var(--iv-bg)',
                                fontSize: '13.5px',
                                fontWeight: 600,
                                color: 'var(--iv-body)',
                                cursor: 'pointer'
                              }}
                            >
                              <option value="lnat">LNAT Path Only</option>
                              <option value="tmua">TMUA Path Only</option>
                              <option value="general">General Workspace</option>
                              <option value="all">All Subscriptions</option>
                            </select>
                          </td>
                          <td style={{ padding: '16px 20px', textAlign: 'center' }}>
                            <button
                              type="button"
                              onClick={() => handleToggleActive(item)}
                              disabled={savingId === item.id}
                              style={{
                                padding: '8px 18px',
                                borderRadius: '999px',
                                border: 'none',
                                background: item.is_active ? 'var(--iv-btn)' : 'var(--iv-btn)',
                                color: 'var(--iv-btn-fg)',
                                fontWeight: 700,
                                fontSize: '13px',
                                cursor: 'pointer',
                                boxShadow: '0 2px 6px color-mix(in srgb, var(--iv-shadow) 10%, transparent)'
                              }}
                            >
                              {item.is_active ? 'ON (Active)' : 'OFF (Hidden)'}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}

          {/* =========================================================================
              TAB 2: CERTIFICATES ISSUANCE & REGISTRY
             ========================================================================= */}
          {activeAdminTab === 'certificates' && (
            <div>
              {/* Header */}
              <div style={{ marginBottom: '28px' }}>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: 'var(--iv-band)', color: 'var(--iv-on-band)', padding: '4px 12px', borderRadius: '999px', fontSize: '12px', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', marginBottom: '8px' }}>
                  IVORI Lab Institutional Registry
                </div>
                <h1 style={{ fontFamily: 'var(--iv-font-head)', fontSize: '32px', margin: 0, color: 'var(--iv-body)' }}>
                  Issue & Manage Certificates
                </h1>
                <p style={{ fontFamily: 'var(--iv-font-body)', fontSize: '15px', color: 'var(--iv-muted)', margin: '6px 0 0 0' }}>
                  Create verifiable certificates for students. The generated link allows students and employers to inspect the authentic credential.
                </p>
              </div>

              {/* Issuance Form Card */}
              <div style={{ background: 'var(--iv-surface)', borderRadius: '16px', border: '1px solid var(--iv-line)', padding: '32px', marginBottom: '32px', boxShadow: '0 4px 16px color-mix(in srgb, var(--iv-shadow) 3%, transparent)' }}>
                <h2 style={{ fontFamily: 'var(--iv-font-head)', fontSize: '22px', margin: '0 0 20px 0', color: 'var(--iv-body)' }}>
                  Issue New Certificate
                </h2>

                <form onSubmit={handleIssueCertificate} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '16px' }}>
                    
                    {/* Student Name */}
                    <div style={{ position: 'relative' }}>
                      <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: 'var(--iv-head)', marginBottom: '6px' }}>
                        Student Name *
                      </label>
                      <input
                        type="text"
                        required
                        placeholder="Type name or email to auto-complete..."
                        value={certStudentName}
                        onChange={(e) => handleStudentSearch(e.target.value)}
                        autoComplete="off"
                        style={{ width: '100%', padding: '10px 14px', border: '1px solid var(--iv-line)', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }}
                      />
                      {studentSuggestions.length > 0 && (
                        <ul style={{
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          right: 0,
                          backgroundColor: 'var(--iv-surface)',
                          border: '1px solid var(--iv-line)',
                          borderRadius: '8px',
                          listStyle: 'none',
                          padding: '4px 0',
                          margin: '4px 0 0',
                          zIndex: 50,
                          maxHeight: '180px',
                          overflowY: 'auto',
                          boxShadow: '0 8px 24px color-mix(in srgb, var(--iv-shadow) 8%, transparent)'
                        }}>
                          {studentSuggestions.map((s) => (
                            <li
                              key={s.user_id}
                              onClick={() => handleSelectStudent(s)}
                              style={{
                                padding: '8px 12px',
                                fontSize: '13px',
                                color: 'var(--iv-body)',
                                cursor: 'pointer',
                                borderBottom: '1px solid var(--iv-line)'
                              }}
                              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--iv-bg)'}
                              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'var(--iv-panel)'}
                            >
                              <strong>{s.full_name}</strong> &lt;{s.email}&gt;
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>

                    {/* Programme */}
                    <div>
                      <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: 'var(--iv-body)', marginBottom: '6px' }}>
                        Programme *
                      </label>
                      <select
                        value={certProgramme}
                        onChange={(e) => setCertProgramme(e.target.value)}
                        style={{ width: '100%', padding: '10px 14px', border: '1px solid var(--iv-line)', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box', background: 'var(--iv-raised)' }}
                      >
                        {PROGRAMME_PRESETS.map((p) => (
                          <option key={p} value={p}>{p}</option>
                        ))}
                        <option value="custom">+ Custom Programme Title</option>
                      </select>
                    </div>

                    {/* Date */}
                    <div>
                      <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: 'var(--iv-body)', marginBottom: '6px' }}>
                        Date *
                      </label>
                      <input
                        type="date"
                        required
                        value={certDate}
                        onChange={(e) => setCertDate(e.target.value)}
                        style={{ width: '100%', padding: '10px 14px', border: '1px solid var(--iv-line)', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }}
                      />
                    </div>

                    {/* Duration */}
                    <div>
                      <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: 'var(--iv-body)', marginBottom: '6px' }}>
                        Duration
                      </label>
                      <input
                        type="text"
                        placeholder="e.g. 35 hours"
                        value={certDuration}
                        onChange={(e) => setCertDuration(e.target.value)}
                        style={{ width: '100%', padding: '10px 14px', border: '1px solid var(--iv-line)', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }}
                      />
                    </div>
                  </div>

                  {certProgramme === 'custom' && (
                    <div>
                      <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: 'var(--iv-body)', marginBottom: '6px' }}>
                        Custom Programme Title *
                      </label>
                      <input
                        type="text"
                        placeholder="e.g. Oxford Biomedical Science Admissions Intensive"
                        value={certCustomProgramme}
                        onChange={(e) => setCertCustomProgramme(e.target.value)}
                        style={{ width: '100%', padding: '10px 14px', border: '1px solid var(--iv-line)', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }}
                      />
                    </div>
                  )}

                  <div>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: 'var(--iv-body)', marginBottom: '6px' }}>
                      Student Email or User ID <span style={{ fontWeight: 400, color: 'var(--iv-muted)' }}>(Optional — automatically binds certificate to student's account)</span>
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. student@gmail.com or 550e8400-e29b-41d4-a716-446655440000"
                      value={certStudentEmail}
                      onChange={(e) => setCertStudentEmail(e.target.value)}
                      style={{ width: '100%', padding: '10px 14px', border: '1px solid var(--iv-line)', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }}
                    />
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: 'var(--iv-head)', marginBottom: '6px' }}>
                        Skills Demonstrated (comma-separated)
                      </label>
                      <input
                        type="text"
                        value={certSkills}
                        onChange={(e) => setCertSkills(e.target.value)}
                        style={{ width: '100%', padding: '10px 14px', border: '1px solid var(--iv-line)', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: 'var(--iv-body)', marginBottom: '6px' }}>
                        Custom Certificate ID <span style={{ fontWeight: 400, color: 'var(--iv-muted)' }}>(Optional — auto-generated if blank)</span>
                      </label>
                      <input
                        type="text"
                        placeholder="e.g. CERT-A7X3K9"
                        value={certCustomId}
                        onChange={(e) => setCertCustomId(e.target.value)}
                        style={{ width: '100%', padding: '10px 14px', border: '1px solid var(--iv-line)', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box', fontFamily: 'monospace' }}
                      />
                    </div>
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'flex-start', marginTop: '10px' }}>
                    <button
                      type="submit"
                      disabled={issuingCert}
                      style={{
                        backgroundColor: 'var(--iv-btn)',
                        color: 'var(--iv-btn-fg)',
                        border: 'none',
                        padding: '12px 28px',
                        borderRadius: '999px',
                        fontWeight: 700,
                        fontSize: '14px',
                        cursor: issuingCert ? 'not-allowed' : 'pointer',
                        opacity: issuingCert ? 0.7 : 1
                      }}
                    >
                      {issuingCert ? 'Issuing Certificate...' : 'Issue Certificate'}
                    </button>
                  </div>
                </form>

                {/* Newly Issued Link Display Card */}
                {issuedCertResult && (
                  <div style={{
                    marginTop: '28px',
                    padding: '20px 24px',
                    backgroundColor: 'var(--iv-bg)',
                    border: '1.5px solid var(--iv-accent)',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: '16px'
                  }}>
                    <div>
                      <div style={{ fontSize: '12px', fontWeight: 800, textTransform: 'uppercase', color: 'var(--iv-accent)', letterSpacing: '0.04em', marginBottom: '4px' }}>
                        Certificate Created Successfully!
                      </div>
                      <div style={{ fontSize: '16px', fontWeight: 700, color: 'var(--iv-accent)' }}>
                        {issuedCertResult.student_name} — {issuedCertResult.programme_name}
                      </div>
                      <div style={{ fontSize: '13px', color: 'var(--iv-body)', marginTop: '4px' }}>
                        Direct Verification Link: <strong style={{ color: 'var(--iv-head)' }}>{appUrl(`/verify/${issuedCertResult.id}`)}</strong>
                      </div>
                    </div>

                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        type="button"
                        onClick={() => copyVerifyLink(issuedCertResult.id)}
                        style={{
                          backgroundColor: 'var(--iv-btn)',
                          color: 'var(--iv-btn-fg)',
                          border: 'none',
                          padding: '8px 16px',
                          borderRadius: '6px',
                          fontWeight: 600,
                          fontSize: '13px',
                          cursor: 'pointer'
                        }}
                      >
                        Copy Link
                      </button>
                      <a
                        href={withAppBasename(`/verify/${issuedCertResult.id}`)}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          backgroundColor: 'var(--iv-surface)',
                          color: 'var(--iv-accent)',
                          border: '1px solid var(--iv-accent)',
                          padding: '8px 16px',
                          borderRadius: '6px',
                          fontWeight: 600,
                          fontSize: '13px',
                          textDecoration: 'none'
                        }}
                      >
                        Open Certificate
                      </a>
                    </div>
                  </div>
                )}
              </div>

              {/* Issued Certificates Registry Table */}
              <div style={{ background: 'var(--iv-surface)', borderRadius: '16px', border: '1px solid var(--iv-line)', overflow: 'hidden', boxShadow: '0 4px 16px color-mix(in srgb, var(--iv-shadow) 3%, transparent)' }}>
                <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--iv-line)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: 'var(--iv-head)' }}>
                    Issued Certificates Registry ({certificatesList.length})
                  </h3>
                  <button
                    type="button"
                    onClick={fetchCertificates}
                    style={{ padding: '6px 14px', background: 'var(--iv-bg)', border: '1px solid var(--iv-line)', borderRadius: '6px', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}
                  >
                    Refresh
                  </button>
                </div>

                {loadingCerts ? (
                  <div style={{ padding: '40px', textAlign: 'center', color: 'var(--iv-muted)' }}>Loading certificates...</div>
                ) : certificatesList.length === 0 ? (
                  <div style={{ padding: '40px', textAlign: 'center', color: 'var(--iv-muted)' }}>
                    No certificates issued yet. Fill out the form above to issue your first certificate!
                  </div>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '14px' }}>
                    <thead>
                      <tr style={{ background: 'var(--iv-bg)', borderBottom: '1px solid var(--iv-line)', color: 'var(--iv-muted)', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                        <th style={{ padding: '14px 20px' }}>Student Name</th>
                        <th style={{ padding: '14px 20px' }}>Programme</th>
                        <th style={{ padding: '14px 20px' }}>Issue Date</th>
                        <th style={{ padding: '14px 20px' }}>Certificate ID</th>
                        <th style={{ padding: '14px 20px', textAlign: 'right' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {certificatesList.map((cert, idx) => (
                        <tr key={cert.id} style={{ borderBottom: idx === certificatesList.length - 1 ? 'none' : '1px solid var(--iv-line)' }}>
                          <td style={{ padding: '14px 20px' }}>
                            <div style={{ fontWeight: 700, color: 'var(--iv-body)' }}>{cert.student_name}</div>
                            {cert.user_id && (
                              <div style={{ fontSize: '11px', color: 'var(--iv-success)', fontWeight: 600, marginTop: '2px', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: 'var(--iv-success)', display: 'inline-block' }}></span>
                                Linked Account: {cert.student_email || cert.user_id.slice(0, 8)}
                              </div>
                            )}
                          </td>
                          <td style={{ padding: '14px 20px', color: 'var(--iv-body)' }}>
                            {cert.programme_name}
                          </td>
                          <td style={{ padding: '14px 20px', color: 'var(--iv-muted)' }}>
                            {cert.issue_date}
                          </td>
                          <td style={{ padding: '14px 20px' }}>
                            <code style={{ background: 'var(--iv-bg)', padding: '2px 8px', borderRadius: '4px', border: '1px solid var(--iv-line)', fontSize: '12px', fontFamily: 'monospace' }}>
                              {cert.id}
                            </code>
                          </td>
                          <td style={{ padding: '14px 20px', textAlign: 'right' }}>
                            <div style={{ display: 'inline-flex', gap: '8px' }}>
                              <button
                                type="button"
                                onClick={() => copyVerifyLink(cert.id)}
                                style={{
                                  background: 'var(--iv-bg)',
                                  border: '1px solid var(--iv-line)',
                                  padding: '6px 12px',
                                  borderRadius: '4px',
                                  fontSize: '12px',
                                  fontWeight: 600,
                                  cursor: 'pointer'
                                }}
                              >
                                Copy Link
                              </button>
                              <a
                                href={withAppBasename(`/verify/${cert.id}`)}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  background: 'var(--iv-band)',
                                  color: 'var(--iv-on-band)',
                                  border: 'none',
                                  padding: '6px 12px',
                                  borderRadius: '4px',
                                  fontSize: '12px',
                                  fontWeight: 600,
                                  textDecoration: 'none'
                                }}
                              >
                                View
                              </a>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}
        </main>
      )}

      <LandingAppStoreFooter />
    </div>
  );
}
